import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type ApiEnvelope } from '@/lib/api/client';

/**
 * The price database — what the POS says an item costs, before the catalogue
 * knows about it.
 *
 * Two things happen here. A price already tied to a catalogue item can be
 * *applied*, pushing the POS figures onto the storefront. A price that is not
 * tied to anything can be *connected*: the API is asked for likely matches, and
 * the answer is either associating it with an item that exists or requesting a
 * new one.
 */

/** `pos/prices/search`. Only one of the three search fields is ever filled. */
export interface PriceSearchModel {
  ItemNumber: string;
  Gtin: string;
  Query: string;
  Page: number;
  Size: number;
  BusinessId: string | null;
}

export const SEARCH_BY = { general: 0, gtin: 1, itemNumber: 2 } as const;

/** `TaxRule`. */
export const TAX_RULES = [
  { value: 0, labelKey: 'itemPrices.taxRule.none' },
  { value: 1, labelKey: 'itemPrices.taxRule.municipal' },
  { value: 2, labelKey: 'itemPrices.taxRule.state' },
  { value: 3, labelKey: 'itemPrices.taxRule.both' },
] as const;

/** How a price is read against its multiple. */
export const PRICE_METHODS = [
  { value: 0, labelKey: 'itemPrices.priceMethod.unit' },
  { value: 1, labelKey: 'itemPrices.priceMethod.split' },
] as const;

/** Only meaningful on a random-weight item. */
export const WEIGHT_TYPES = [
  { value: 0, labelKey: 'itemPrices.weightType.lb' },
  { value: 4, labelKey: 'itemPrices.weightType.unit' },
] as const;

export function taxRuleLabelKey(rule: number | null | undefined) {
  return (
    TAX_RULES.find((option) => option.value === rule)?.labelKey ??
    'itemPrices.unknown'
  );
}

export function priceMethodLabelKey(method: number | null | undefined) {
  return (
    PRICE_METHODS.find((option) => option.value === method)?.labelKey ??
    'itemPrices.unknown'
  );
}

/** One row of the price database. */
export interface ItemPrice {
  id: string;
  itemNumber: string | null;
  gtin: string | null;
  upc: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  internalName: string | null;
  department: string | null;
  aisle: string | null;
  sectionId: string | null;
  quantityOnHand: number | null;
  searchTags: string | null;
  salesRank: number | null;
  createdAt: string | null;
  editedAt: string | null;

  /** True once the price is tied to a catalogue item for this store. */
  isConnected: boolean;
  itemId: string | null;
  itemRequestId: string | null;

  taxRule: number | null;
  basePrice: number | null;
  baseMultiple: number | null;
  basePriceMethod: number | null;
  cost: number | null;
  pack: number | null;
  stateTaxPct: number | null;
  munTaxPct: number | null;

  promoPrice: number | null;
  promoMultiple: number | null;
  promoPriceMethod: number | null;
  promoStartDate: string | null;
  promoEndDate: string | null;

  isEbt: boolean;
  idRequired: boolean;
  isAlcoholicBeverage: boolean;
  isVisible: boolean;
  isAvailable: boolean;
  isDeleted: boolean;
  isPackagingSensitive: boolean;
  isTemperatureSensitive: boolean;
  isPerishable: boolean;

  isAllNatural: boolean;
  isLactoseFree: boolean;
  isSugarFree: boolean;
  isKeto: boolean;
  isOrganic: boolean;
  isVegan: boolean;
  isVegetarian: boolean;
  isGlutenFree: boolean;
  isGrassFed: boolean;
  isAntioxidant: boolean;
  isNutFree: boolean;
  isNonGmo: boolean;

  hasRandomWeight: boolean;
  madeLocally: boolean;
  weightType: number | null;
  minQuantity: number | null;
  maxQuantity: number | null;
  stepAmount: number | null;
  unitPriceFactor: number | null;
}

export function usePriceSearch(model: PriceSearchModel) {
  return useQuery({
    queryKey: ['item-prices', 'search', model],
    queryFn: () =>
      unwrap<{ items: ItemPrice[]; totalItems: number }>(
        api.post('pos/prices/search', model)
      ),
    placeholderData: keepPreviousData,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['item-prices'] });
}

/** Push the POS figures onto the storefront for one price. */
export function useApplyPrice() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ upc, businessId }: { upc: string; businessId: string | null }) =>
      unwrap<unknown>(
        api.post(
          `pos/prices/apply/${upc}${businessId ? `/${businessId}` : ''}`,
          {}
        )
      ),
    onSuccess: invalidate,
  });
}

/** Catalogue items that might already be this price. */
export interface PotentialMatch {
  id: string;
  itemId?: string | null;
  itemRequestId?: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  itemImage: string | null;
}

export function useCheckForMatches() {
  return useMutation({
    mutationFn: (priceId: string) =>
      unwrap<{ items: PotentialMatch[] }>(
        api.get<ApiEnvelope<{ items: PotentialMatch[] }>>(
          `businesses/catalog/newproducts/check/${priceId}`
        )
      ),
  });
}

/**
 * Everything a store needs to say about an item it stocks: where it sits, what
 * it costs, how it is taxed and what it is made of.
 *
 * Both flows below carry this — a new item request nests it under
 * `businessItemAssociation`, an association posts it flat — which is why it is
 * one type and one set of fields rather than two that drifted.
 */
export interface BusinessItemAssociation {
  businessId: string | null;
  inStoreId: string | null;
  upc: string | null;
  isVisible: boolean;
  isAvailable: boolean;
  sectionId: string | null;
  department: string | null;
  aisle: string | null;
  stateTaxPct: number | null;
  munTaxPct: number | null;

  isAllNatural: boolean;
  isLactoseFree: boolean;
  isSugarFree: boolean;
  isKeto: boolean;
  isOrganic: boolean;
  isVegan: boolean;
  isVegetarian: boolean;
  isGlutenFree: boolean;
  isGrassFed: boolean;
  isAntioxidant: boolean;
  isNutFree: boolean;
  isNonGmo: boolean;

  hasRandomWeight: boolean;
  madeLocally: boolean;
  weightType: number | null;
  minQuantity: number | null;
  maxQuantity: number | null;
  stepAmount: number | null;
  unitPriceFactor: number | null;

  taxRule: number | null;
  basePriceMethod: number | null;
  basePrice: number | null;
  baseMultiple: number | null;
  promoPrice: number | null;
  promoMultiple: number | null;
  promoPriceMethod: number | null;
  promoStartDate: string | null;
  promoEndDate: string | null;
  cost: number | null;
  pack: number | null;
}

/** The item itself — what every store selling it shares. */
export interface ItemRequest {
  suggestedDepartment: string | null;
  internalRank: number | null;
  gtin: string | null;
  brand: string | null;
  name: string | null;
  description: string | null;
  itemImage: string | null;
  itemThumbnail: string | null;
  searchTags: string | null;
  internalId: string | null;
  isPackagingSensitive: boolean;
  isTemperatureSensitive: boolean;
  isAlcoholicBeverage: boolean;
  isPerishable: boolean;
  isEbt: boolean;
  idRequired: boolean;
  businessItemAssociation: BusinessItemAssociation;
}

/**
 * A promo range is entered as local dates and stored in UTC. Empty stays empty
 * — an item with no promotion must not acquire one made of today's date.
 */
export function toUtc(value: string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

export function useCreateItemRequest() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (request: ItemRequest) =>
      unwrap<unknown>(
        api.post('businesses/catalog/newproducts/itemRequests/create', {
          ...request,
          businessItemAssociation: {
            ...request.businessItemAssociation,
            promoStartDate: toUtc(request.businessItemAssociation.promoStartDate),
            promoEndDate: toUtc(request.businessItemAssociation.promoEndDate),
          },
        })
      ),
    onSuccess: invalidate,
  });
}

/**
 * Tie this price to something that already exists.
 *
 * Which endpoint depends on what it is being tied to: a catalogue item that is
 * live, or an item request somebody already raised and that has not landed yet.
 */
export function useAssociateItem() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      itemId,
      itemRequestId,
      association,
    }: {
      itemId: string | null;
      itemRequestId: string | null;
      association: BusinessItemAssociation;
    }) => {
      const path = itemRequestId
        ? `businesses/catalog/newproducts/itemRequests/associate/${itemRequestId}`
        : `businesses/catalog/newproducts/item/associate/${itemId}`;
      return unwrap<unknown>(
        api.post(path, {
          ...association,
          itemId,
          itemRequestId,
          promoStartDate: toUtc(association.promoStartDate),
          promoEndDate: toUtc(association.promoEndDate),
        })
      );
    },
    onSuccess: invalidate,
  });
}

// -------------------------------------------------- pickers and uploads

export interface StoreSection {
  id: string;
  nameEng: string | null;
  nameSpa: string | null;
}

export function useStoreSections(businessId: string | null) {
  return useQuery({
    queryKey: ['item-prices', 'sections', businessId],
    queryFn: () =>
      unwrap<{ results: StoreSection[] }>(
        api.get<ApiEnvelope<{ results: StoreSection[] }>>(
          `businesses/${businessId}/menusections`
        )
      ),
    select: (data) => data.results ?? [],
    enabled: !!businessId,
  });
}

export function useStoreAisles(businessId: string | null) {
  return useQuery({
    queryKey: ['item-prices', 'aisles', businessId],
    queryFn: () =>
      unwrap<{ results: string[] }>(
        api.get<ApiEnvelope<{ results: string[] }>>(
          `businesses/${businessId}/aisle`
        )
      ),
    select: (data) => data.results ?? [],
    enabled: !!businessId,
  });
}

export function useUploadItemMedia() {
  return useMutation({
    mutationFn: (file: File) => {
      const body = new FormData();
      body.append('file', file);
      return unwrap<string>(
        api.post('utility/itemMedia/uploadfile', body, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      );
    },
  });
}
